import { ContactCellPhonePutRequest } from "@/models/users/contact-cellphone-put-request";
import { ContactEmailPutRequest } from "@/models/users/contact-email-put-request";
import { LoginRequest } from "@/models/users/login-request";
import { ProfileEnum } from "@/providers/enums/profile-Enum";
import { PageResponse } from "@/providers/models/page-response";
import { PageDateRequest } from "@/providers/models/pageDateRequest";
import { resetLoginRequest } from "@/providers/models/resetLoginRequest";
import { SellerResponseDto, UserDriversResponse, Usuario } from "@/providers/models/usuario";
import { UsuarioLogin } from "@/providers/models/usuarioLogin";
import { HttpClientService } from "@/providers/services/http-client.service";
import { Injectable } from "@angular/core";

@Injectable({
    providedIn: "root",
})
export class UserService {
    constructor(private httpClient: HttpClientService) { }

    async getUserById(usuarioId: number): Promise<Usuario> {
        return this.httpClient.get("/Usuario", `/${usuarioId}`);
    }

    async userLogin(request: LoginRequest): Promise<PageResponse<UsuarioLogin>> {
        return this.httpClient.post(`/UsuarioLogin`, "/login", request, false);
    }

    async checkEmailIfExists(email: string): Promise<boolean> {
        return this.httpClient.get(`/Usuario`, `/check-email/${btoa(email)}`, false);
    }

    async checkCellPhoneIfExists(cellPhone: string): Promise<boolean> {
        return this.httpClient.get(`/Usuario`, `/check-cellphone/${cellPhone}`, false);
    }

    async checkDocumentIfExists(document: string): Promise<boolean> {
        return this.httpClient.get(`/Usuario`, `/check-document/${document}`, false);
    }

    async validateCodeUser(userEmail: string, generatedCode: string): Promise<boolean> {
        return this.httpClient.post("/Usuario", `/validar/codigo-gerado/${userEmail}/${generatedCode}`, "");
    }

    async updateContactEmail(request: ContactEmailPutRequest): Promise<void> {
        return this.httpClient.put(`/Usuario`, "/contact", request, false);
    }

    async updateContactCellPhone(request: ContactCellPhonePutRequest): Promise<void> {
        return this.httpClient.put(`/Usuario`, "/contact/verify", request, false);
    }

    async validateUser(resetLogin: resetLoginRequest): Promise<Usuario> {
        return this.httpClient.post("/Usuario", `/validar/dados/usuario`, resetLogin);
    }

    async getUsersByDates(pageDateRequest: PageDateRequest): Promise<Usuario[]> {
        return this.httpClient.post("/Usuario", `/usuarios/datas`, pageDateRequest);
    }

    async getAllUsers(): Promise<Usuario[]> {
        return this.httpClient.get("/Usuario", "");
    }

    async getAllSellers(): Promise<SellerResponseDto[]> {
        return this.httpClient.get("/Usuario", `/vendedores-lista`);
    }

    async getDrivers(): Promise<UserDriversResponse[]> {
        return this.httpClient.get("/Usuario", `/motoristas-lista`);
    }

    async getTechnicalOperators(): Promise<Usuario[]> {
        return this.httpClient.get("/Usuario", `/operadores-tecnicos`);
    }

    async getAllManagersSupport(): Promise<Usuario[]> {
        return this.httpClient.get("/Usuario", `/gerentes-suporte`);
    }

    async getAllManagersAndSupervisors(): Promise<Usuario[]> {
        return this.httpClient.get("/Usuario", `/gerentes-supervisores-lista`);
    }

    async getUserId(userId: number): Promise<Usuario> {
        return this.httpClient.get("/Usuario", `/${userId}`);
    }

    async verifyEmail(email: string): Promise<boolean> {
        return this.httpClient.get("/Usuario", `/email/${email}`);
    }

    async verifyCpf(cpf: string): Promise<boolean> {
        return this.httpClient.get("/Usuario", `/cpf/${cpf}`);
    }

    async getUserByName(usuarioNome: string): Promise<Usuario> {
        return this.httpClient.get(`/Usuario`, `/nome/${usuarioNome}`);
    }

    async getAllUserByNameAndProfile(perfil: ProfileEnum): Promise<Usuario[]> {
        return this.httpClient.get("/Usuario", `/por/nome/perfil/${perfil}`);
    }

    async getUserByNameSurname(usuarioNomeSobrenome: string): Promise<Usuario> {
        return this.httpClient.get(`/Usuario`, `/nome-sobrenome/${usuarioNomeSobrenome}`);
    }

    async userUpdateProfile(usuario: Usuario): Promise<Usuario> {
        return this.httpClient.put(`/Usuario`, `/atualizar-usuario-profile`, usuario);
    }

    async saveImage(usuarioId: number, usuarioLogadoId: number, file: File): Promise<Usuario> {
        const fileToUpload = file as File;
        const formData = new FormData();
        formData.append("file", fileToUpload);

        return this.httpClient.post(`/Usuario`, `/upload-image/${usuarioId}/${usuarioLogadoId}`, formData);
    }

    async resendEmailUserRegistration(usuarioId: number): Promise<boolean> {
        return this.httpClient.post(`/Usuario`, `/reenvio/email/cadastro/${usuarioId}`, true);
    }

    async updateStatusToolUser(usuarioId: number, statusFerramenta: boolean, usuarioLogadoId: number): Promise<Usuario> {
        return this.httpClient.put("/Usuario", `/atualizar/status/ferramenta/${statusFerramenta}/${usuarioId}/${usuarioLogadoId}`, usuarioId);
    }

    async updateStatusUser(usuario: Usuario, status: boolean, usuarioLogadoId: number): Promise<Usuario> {
        return this.httpClient.put(`/Usuario`, `/atualizar-status/${status}/${usuarioLogadoId}`, usuario);
    }

    async addUser(usuario: Usuario): Promise<Usuario> {
        return this.httpClient.post("/Usuario", "", usuario);
    }

    async updateUser(usuario: Usuario, usuarioLogadoId: number): Promise<Usuario> {
        return this.httpClient.put("/Usuario", `/atualizar-usuario/${usuario.id}/${usuarioLogadoId}`, usuario);
    }

    async allListDownloadVendedores(): Promise<any> {
        return this.httpClient.get("/Usuario", `/export-vendedores`, { responseType: "blob" });
    }

    async allListDownloadUsuarios(): Promise<any> {
        return this.httpClient.get("/Usuario", `/export-usuarios`, { responseType: "blob" });
    }

    async activeDeactiveUser(usuarioId: number, usuarioLogadoId: number, indice?: boolean): Promise<Usuario> {
        return this.httpClient.put("/Usuario",  `/id/${indice}/${usuarioId}/${usuarioLogadoId}`, '');
    }
}
